import React, { useState } from 'react';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../context/AuthContext';
import { Mail, Lock, User, ArrowRight, Loader2, CheckCircle, Eye, EyeOff } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card } from '../components/ui/card';
import FlagToggle from '../components/FlagToggle';

const SAFE_NEXT = /^\/(?!\/)[a-zA-Z0-9/_?=&-]*$/;
// Post-Signup Immigration Activation Fix (2026-02-20): default landing
// for a fresh Basic user is /mos-precheck — the only currently working
// first meaningful action. /app remains in the allowlist so a backend
// config or explicit ?next=/app still works.
const ALLOWED_BACKEND_ROUTES = new Set(['/mos-precheck', '/app']);
const DEFAULT_POST_SIGNUP_ROUTE = '/mos-precheck';

const Register = () => {
  const navigate = useNavigate();
  const { register } = useAuth();
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const rawNext = searchParams.get('next');
  const explicitNext = rawNext && SAFE_NEXT.test(rawNext) ? rawNext : null;
  
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    name: ''
  });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    const result = await register(formData.email, formData.password, formData.name);

    if (result.success) {
      // Resolution order (same as Login.jsx):
      //   1. Explicit ?next=… (deep-link wins).
      //   2. Owner emails become admin at registration → send to /analytics.
      //   3. Backend cohort default (`next_route`), validated against the
      //      allowlist.
      //   4. Hardcoded /app fallback.
      let destination = DEFAULT_POST_SIGNUP_ROUTE;
      if (explicitNext) {
        destination = explicitNext;
      } else if (result.role === 'admin') {
        destination = '/analytics';
      } else if (result.nextRoute && ALLOWED_BACKEND_ROUTES.has(result.nextRoute)) {
        destination = result.nextRoute;
      }
      navigate(destination);
    } else {
      setError(result.error);
    }
    setLoading(false);
  };

  return (
    <div className="hussar-auth-page">
      <div className="hussar-auth-toolbar">
        <FlagToggle />
      </div>
      <div className="hussar-auth-container">
        <div className="hussar-auth-header">
          <h1 className="hussar-auth-title">{t('auth.registerTitle')}</h1>
          <p className="hussar-auth-subtitle">
            {t('auth.registerSubtitle')}
          </p>
          <p className="hussar-auth-trust">
            <Lock className="w-3 h-3" />
            <span>{t('auth.trustNoCard')}</span>
          </p>
        </div>

        <Card className="hussar-auth-card">
          <form onSubmit={handleSubmit} className="hussar-auth-form">
            {error && (
              <div className="hussar-auth-error">
                {error}
              </div>
            )}

            <div className="hussar-form-group">
              <label className="hussar-form-label">{t('auth.labelName')}</label>
              <Input
                type="text"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder={t('auth.placeholderName')}
                required
                className="hussar-form-input"
              />
            </div>

            <div className="hussar-form-group">
              <label className="hussar-form-label">{t('auth.labelEmail')}</label>
              <Input
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                placeholder={t('auth.placeholderEmail')}
                required
                className="hussar-form-input"
              />
            </div>

            <div className="hussar-form-group">
              <label className="hussar-form-label">{t('auth.labelPassword')}</label>
              <div className="hussar-password-wrap">
                <Input
                  type={showPassword ? 'text' : 'password'}
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  placeholder={t('auth.placeholderPasswordMin')}
                  required
                  minLength={6}
                  className="hussar-form-input hussar-form-input-password"
                  data-testid="register-password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword((v) => !v)}
                  className="hussar-password-toggle"
                  aria-label={showPassword ? t('auth.hidePassword') : t('auth.showPassword')}
                  aria-pressed={showPassword}
                  tabIndex={0}
                  data-testid="register-password-toggle"
                >
                  {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
            </div>

            <Button
              type="submit"
              disabled={loading}
              className="hussar-btn-primary hussar-btn-full hussar-btn-auth"
            >
              {loading ? (
                <>
                  <Loader2 className="w-5 h-5 animate-spin" />
                  {t('auth.loadingRegister')}
                </>
              ) : (
                <>
                  {t('auth.submitRegister')}
                  <ArrowRight className="w-5 h-5" />
                </>
              )}
            </Button>
            <p className="hussar-auth-trust hussar-auth-trust-footer">
              <CheckCircle className="w-3 h-3" />
              <span>{t('auth.trustStaysFree')}</span>
            </p>
          </form>
        </Card>

        <p className="hussar-auth-switch">
          {t('auth.haveAccount')}{' '}
          <Link to={`/login?next=${encodeURIComponent(explicitNext || '/app')}`} className="hussar-auth-link">
            {t('auth.switchToLogin')}
          </Link>
        </p>
      </div>
    </div>
  );
};

export default Register;
